import { withTopbarProps } from '@jusbrasil/topbar'
import { withApollo } from '@jusbrasil-web/core-apollo'
import { ARGOS_ENDPOINT } from '@jusbrasil-web/shared-ab-test'
import { ArgosClient } from '@jusbrasil-web/shared-argos-client'
import { markSignupPhoneStepSeen } from '@jusbrasil-web/shared-auth-flows'
import { parseUserCookie, withUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'
import { GetServerSideProps } from 'next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useEditProfilePhoneForSignup } from '../../../api'
import { AuthLayout, LoginCard, PhoneStep } from '../../../components'
import { ROUTES } from '../../../constants'
import { getCampaignFeatureCode } from '../../../utils/campaign-attribution'
import { buildUserAccountEventData } from '../../../utils/event-data'
import {
  type FakePhoneReason,
  getObviouslyFakePhoneReason,
} from '../../../utils/get-obviously-fake-phone-reason'
import { logNextUrlCollapse } from '../../../utils/next-url-collapse-logger'
import { PHONE_STEP_EVENTS } from '../../../utils/phone-step-events'
import { redirectTo } from '../../../utils/redirect'
import { getSafeRedirectUrl } from '../../../utils/redirect-url'
import type { GetSignupPhoneStepProfileQuery } from './__generated__/queries.graphql'
import { GET_SIGNUP_PHONE_STEP_PROFILE } from './queries'

// ArgosClient uses navigator.sendBeacon — survives page unload, unlike
// shared-utils-events sendMetrics which queues onto window._argos.
const browserArgosClient = new ArgosClient(ARGOS_ENDPOINT)

// An invalid_subscriber_prefix number can be one digit away from a real phone
// (e.g. mobile typed without the leading 9), so only the DDD and the flagged
// subscriber prefix reach analytics (LGPD data minimization).
function toEventPhone(digits: string, reason: FakePhoneReason): string {
  if (reason !== 'invalid_subscriber_prefix') return digits
  return digits.slice(0, 3) + '*'.repeat(digits.length - 3)
}

export interface PhoneStepPageProps {
  nextUrl: string
  featureCode?: string
}

export function PhoneStepPage({ nextUrl, featureCode }: PhoneStepPageProps) {
  const eventData = useMemo(() => buildUserAccountEventData({ featureCode }), [featureCode])
  const { execute, loading } = useEditProfilePhoneForSignup()
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const seenFiredRef = useRef(false)
  const actionTakenRef = useRef(false)

  const redirectOut = useCallback(() => {
    redirectTo(nextUrl)
  }, [nextUrl])

  useEffect(() => {
    if (seenFiredRef.current) return
    seenFiredRef.current = true
    markSignupPhoneStepSeen()
    sendMetrics(PHONE_STEP_EVENTS.SEEN, null, eventData)
  }, [eventData])

  useEffect(() => {
    // Defer mounted past Strict Mode's double-invoke so the throwaway cleanup
    // does not fire CLOSED on a real session.
    let mounted = false
    const mountTimer = setTimeout(() => {
      mounted = true
    }, 0)
    return () => {
      clearTimeout(mountTimer)
      if (mounted && !actionTakenRef.current) {
        browserArgosClient.send(undefined, PHONE_STEP_EVENTS.CLOSED, eventData)
      }
    }
  }, [eventData])

  const handleActivate = useCallback(
    async (digits: string) => {
      setPhoneError(null)
      const fakeReason = getObviouslyFakePhoneReason(digits)
      if (fakeReason) {
        // Deliberately indistinguishable from success: an inline error would
        // teach the user to submit a plausible fake instead.
        actionTakenRef.current = true
        sendMetrics(PHONE_STEP_EVENTS.FAKE_SUBMITTED, null, {
          ...eventData,
          metadata: { reason: fakeReason, phone: toEventPhone(digits, fakeReason) },
        })
        redirectOut()
        return
      }
      const errorMsg = await execute(digits)
      if (errorMsg) {
        setPhoneError(errorMsg)
        return
      }
      actionTakenRef.current = true
      sendMetrics(PHONE_STEP_EVENTS.ACTIVATED, null, eventData)
      redirectOut()
    },
    [execute, redirectOut, eventData]
  )

  const handleSkip = useCallback(() => {
    actionTakenRef.current = true
    sendMetrics(PHONE_STEP_EVENTS.SKIPPED, null, eventData)
    redirectOut()
  }, [redirectOut, eventData])

  return (
    <AuthLayout>
      <LoginCard hideCloseButton>
        <PhoneStep
          onActivate={handleActivate}
          onSkip={handleSkip}
          isLoading={loading}
          error={phoneError ?? undefined}
        />
      </LoginCard>
    </AuthLayout>
  )
}

const getServerSideProps: GetServerSideProps<PhoneStepPageProps> = async (ctx) => {
  const session = parseUserCookie(ctx)
  if (!session?.uid) {
    return {
      redirect: { destination: ROUTES.SIGN_UP.BEGIN, permanent: false },
    }
  }

  const rawNextUrl = getFirstQueryParam(ctx.query.next_url) || null
  const nextUrl = getSafeRedirectUrl(rawNextUrl)
  logNextUrlCollapse({ step: 'phone-step', raw: rawNextUrl, jdid: ctx.req.cookies?.jdid ?? null })

  const isB2BScope = getFirstQueryParam(ctx.query.scope).trim().toLowerCase() === 'b2b'
  if (isB2BScope) {
    return { redirect: { destination: nextUrl, permanent: false } }
  }

  const { client: apolloClient, withApolloProps } = withApollo(ctx)

  const profileResult = await apolloClient
    .query<GetSignupPhoneStepProfileQuery>({ query: GET_SIGNUP_PHONE_STEP_PROFILE })
    .catch((err) => {
      console.error(
        '[phone-step] Failed to fetch mainPhone profile, rendering page fail-open:',
        err
      )
      return null
    })

  if (profileResult?.data?.root?.viewer?.user?.profile?.mainPhone?.phone) {
    return { redirect: { destination: nextUrl, permanent: false } }
  }

  const featureCode = getCampaignFeatureCode(ctx.query, ctx)

  return {
    props: {
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      ...withApolloProps(),
      nextUrl,
      ...(featureCode && { featureCode }),
    },
  }
}

export const PhoneStepPageExports = { getServerSideProps }
